import * as React from "react";
import { Link } from "react-router";
import * as ReactDOM from "react-dom";
import {
  PrimaryButton,
  ButtonSize,
  Color,
  IconName,
  Formsy,
  ErrorBlock,
  Size,
  Paragraph,
  TextColor,
  Icon,
  IconSize,
  Title,
  Space
} from "@root/shared";
import { LoginShellFull } from "./login-shell-full";
import { ILinkItem } from "./link-item";
import { LoginProps } from "@lib/common-interfaces";
import { LoginUrlHelper, ReturnUrls } from "./utils/login-url-helper";
import { LoginButtons } from "./login-buttons/login-buttons";
import { AppInvitationCard } from "client/login/app-invitation-card/app-invitation-card";
import { LiveRegion } from "@root/shared";
import { telemetry } from "@root/lib/telemetry";

const styles = require("./login.scss");

export interface LoginState {
  errorMessage?: string;
  signinWithEmail?: boolean;
  signinAltText?: string;
  isFirstLoad?: boolean;
}

export class Login extends React.Component<{}, LoginState> {
  public initProps: LoginProps;
  private _form: any;

  constructor(props) {
    super(props);
    this.state = {
      errorMessage: "",
      signinWithEmail: false,
      signinAltText: "Sign in with email",
      isFirstLoad: true
    };
  }

  public validateForm = (values: any) => {
    const emailInput = this._form.inputs.email.input;
    const passwordInput = this._form.inputs.password.input;

    if (!values.email && !values.password) {
      emailInput.focus();
      this.setState({
        errorMessage: "Email and password are required."
      });
    } else if (!values.email) {
      emailInput.focus();
      this.setState({
        errorMessage: "Email or username is required."
      });
    } else if (!values.password) {
      passwordInput.focus();
      this.setState({
        errorMessage: "Password is required."
      });
    }
  };

  public onSubmit = (form: any, e: any) => {
    const myWindow = window as any;
    const { initProps = {} } = myWindow;
    const fromHockeyApp = initProps.fromHockeyApp;
    if (fromHockeyApp) {
      telemetry.trackEvent(`HockeyApp authentication redirect engage`, { buttonName: "Sign In", url: window.location.pathname });
    }
    (ReactDOM.findDOMNode(this._form) as any).submit();
  };

  public showErrorMessage(message: string) {
    return (
      <LiveRegion role="alert" active={true}>
        <ErrorBlock>{message}</ErrorBlock>
      </LiveRegion>
    );
  }

  public UNSAFE_componentWillMount() {
    const { initProps = {} } = window as any;
    this.initProps = initProps;
    this.setState({
      errorMessage: this.initProps.message
    });
    telemetry.trackPage("/sign-in");
  }

  public componentWillUnmount() {
    this.initProps.message = null;
    this.initProps.email = null;
  }

  private renderTitleArea = (): React.ReactNode => {
    const { fromHockeyApp, appInvitation } = this.initProps;
    return (
      <>
        {appInvitation ? <AppInvitationCard {...appInvitation} /> : null}

        {fromHockeyApp ? (
          <div className={styles.hockeyappContainer}>
            <div className={styles.hockeyapp}>
              <Icon color={Color.White} size={IconSize.Medium} icon={IconName.HockeyappInvert} />
            </div>
            <div>
              Sign in with your
              <br />
              HockeyApp account.
            </div>
          </div>
        ) : null}

        {!fromHockeyApp && !appInvitation ? <Title size={Size.Small}>Sign in</Title> : null}
      </>
    );
  };

  public clearErrorMessage = () => {
    this.setState({ errorMessage: "" });
  };

  public toggleSigninChoice = () => {
    const signinWithEmail = this.state.signinWithEmail;
    signinWithEmail
      ? this.setState({ signinWithEmail: false, signinAltText: "Sign in with email" })
      : this.setState({ signinWithEmail: true, signinAltText: "Quick sign in" });
  };

  private renderSignInForm = () => {
    const { errorMessage } = this.state;
    const { email, csrfToken } = this.initProps;
    const emailName = email;
    const signinText = "Sign in";

    return (
      <Formsy.Form
        ref={(ref) => (this._form = ref)}
        key="login-form"
        method="post"
        onInvalidSubmit={this.validateForm}
        onValidSubmit={this.onSubmit}
        className={styles["login-form"]}
      >
        <div>
          <input name="_csrf" type="hidden" value={csrfToken} />
          <Formsy.Input
            groupClassName={styles.field}
            name="email"
            type="text"
            defaultValue={emailName}
            autoFocus={!emailName}
            autoCorrect="none"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck="false"
            placeholder="Email or username"
            isRequired
            data-test-id="login-input-username"
          />
          <Formsy.Input
            groupClassName={styles.field}
            name="password"
            type="password"
            autoFocus={!!emailName}
            placeholder="Password"
            isRequired
            data-test-id="login-input-password"
          />
        </div>
        {errorMessage ? this.showErrorMessage(errorMessage) : null}
        <div className={styles["form-actions"]}>
          <PrimaryButton
            size={ButtonSize.Small}
            type="submit"
            formNoValidate
            className={styles["submit-button"]}
            data-test-id="login-button"
            onClick={this.clearErrorMessage}
          >
            {signinText}
          </PrimaryButton>
          <Paragraph size={Size.Medium} color={TextColor.Secondary}>
            <Link className={styles["forgot-password"]} to="/reset">
              Forgot password?
            </Link>
          </Paragraph>
        </div>
      </Formsy.Form>
    );
  };

  public render() {
    if (this.state.isFirstLoad && !!this.state.errorMessage) {
      this.setState({
        signinAltText: "Quick sign in",
        isFirstLoad: false,
        signinWithEmail: true
      });
    }

    const myWindow = window as any,
      currLocation = myWindow.location || {},
      returnUrls: ReturnUrls = LoginUrlHelper.buildReturnUrls(currLocation.search, this.initProps),
      bottomLinks: ILinkItem[] = [
        {
          route: "https://azure.microsoft.com/en-us/support/legal",
          title: "Terms of use"
        }
      ],
      externalBottomLinks: ILinkItem[] = [
        {
          route: "https://aka.ms/appcenterprivacy",
          title: "Privacy"
        },
        {
          route: "https://status.appcenter.ms/",
          title: "Status"
        }
      ];

    const { fromHockeyApp, organizationInvitation } = this.initProps;
    const subtitleArea = (() => {
      if (organizationInvitation) {
        return (
          <div>
            {organizationInvitation.inviterName} invited you to join{" "}
            <span className={styles["invitation-info"]}>{organizationInvitation.organizationDisplayName}</span>.
          </div>
        );
      } else if (fromHockeyApp) {
        return <div>Use your existing HockeyApp credentials to access App Center.</div>;
      } else {
        return null;
      }
    })();

    const titleArea = this.renderTitleArea();
    const signinWithEmail: boolean = this.state.signinWithEmail;

    return (
      <div data-test-id="login-page" id="content" role="main">
        <LoginShellFull
          linkItems={bottomLinks}
          externalLinkItems={externalBottomLinks}
          titleArea={titleArea}
          subtitleArea={subtitleArea}
        >
          <div className={styles["sign-in-box-margin"]}>
            {!signinWithEmail || fromHockeyApp ? <LoginButtons /> : null}
            {fromHockeyApp ? (
              <div className={styles["divider"]}>
                <div className={styles["middle-line"]} />
                <div className={styles["or-label"]}>or</div>
              </div>
            ) : null}
            {signinWithEmail || fromHockeyApp ? this.renderSignInForm() : null}
          </div>
          <div className={styles["account-action-wrapper"]} data-test-id={"bottom-links"}>
            <Link className={styles["account-action"]} to={"/create-account" + returnUrls.original}>
              <Paragraph
                color={TextColor.Link}
                size={Size.Medium}
                className={styles["account-action-link-wrapper"]}
                spaceBelow={Space.Medium}
              >
                Create a new account <Icon color={Color.Blue} size={IconSize.Small} icon={IconName.ButtonChevronRight} />
              </Paragraph>
            </Link>
            <Link
              className={styles["account-action"]}
              to={"/sign-in" + window.location.search}
              onClick={this.toggleSigninChoice}
              hidden={fromHockeyApp}
            >
              <Paragraph
                color={TextColor.Link}
                size={Size.Medium}
                className={styles["account-action-link-wrapper"]}
                data-test-id="login-with-credentials-link"
              >
                {this.state.signinAltText} <Icon color={Color.Blue} size={IconSize.Small} icon={IconName.ButtonChevronRight} />
              </Paragraph>
            </Link>
          </div>
        </LoginShellFull>
      </div>
    );
  }
}
